/**
 * `/experience` — **Application experience: the browser rollout** (L1).
 *
 * Five apps — Dynamics 365 F&O and CE, Power BI, SharePoint and OneDrive,
 * Teams — measured by **one managed Edge extension**. Nothing else on this
 * board measures anything: no extension on a machine means no row for that
 * machine, for every panel here. So the board's subject is the rollout first
 * and the measurements second, in that order on the page:
 *
 * - **Section A — Who reports.** How many of the five apps have a reporting
 *   browser at all, what share of active F&O users is covered, how many
 *   user-minutes of evidence exist, and — because a flat line nobody can act on
 *   is worse than no line — what would make those numbers grow.
 * - **Section B — What those browsers measured.** The per-app tiles, the hourly
 *   TTFB trend, the worst page keys and the idle probes, each carrying the size
 *   of the sample it rests on. Scoped to the browsers in Section A, never to
 *   "the estate".
 * - **Footer — Data sources.** The extension's intake and the two NOVA
 *   collectors, with their freshness and a "Run now". It moved here from the top
 *   of the Dynamics board on 2026-08-21, because collectors, RUM posts and
 *   freshness are this board's subject and because a business board must not
 *   lead with the health of its own plumbing. It renders outside the access gate
 *   below, on purpose: when this board cannot read its workspace is exactly when
 *   someone needs to know whether the collectors ran.
 *
 * ## Why the order was inverted (2026-08-22)
 *
 * The board used to open with per-app latency tiles under the heading "what the
 * estate's browsers measure". Measured the same day: **one** of 31 F&O companies
 * and 17 of 269,725 form loads had a reporting browser. Confident headings over
 * near-empty charts invite exactly the wrong conclusion — that the apps are
 * fine — so coverage became the subject and every measurement panel below now
 * states the sample it stands on. Nothing was removed; the panels were rescoped.
 *
 * ## The apps with no row at all
 *
 * The payload's `apps` list carries **only the apps that produced rows** (the
 * KQL summarises by app, so an app nobody reported for is absent, not zero). A
 * board that rendered the payload verbatim would therefore show one tile and
 * silently drop the four dark apps — the flattering direction (design rules §8).
 * `rolloutRows()` puts the contract's five back, each as an explicitly silent
 * row, and keeps any sixth app the extension starts reporting on.
 *
 * ## What this board deliberately cannot tell you
 *
 * Nothing here names a person. The payload carries counts, medians and shares
 * only — the per-user layer is `/dynamics/investigate`, behind the reason gate
 * and the investigator allow-list.
 *
 * It also cannot tell you which **version** of the extension a browser runs, or
 * how many **machines** report. `BrowserRUM_CL` holds `ExtVersion` and
 * `InstallId` on every row, but no read endpoint groups by either, so a fleet
 * still on an old build looks identical here to a fleet on the newest, and every
 * count on this board is distinct *people* (`dcount(Mail)`), not devices. Both
 * gaps are stated on the board rather than papered over.
 *
 * It also does not repeat the F&O server side. F&O appears here as one of five
 * apps, measured in the browser; availability, form percentiles, result codes,
 * queue backlog and the slow-interaction classification belong to `/dynamics`,
 * and the row links across rather than drawing them again. The rule is one owner
 * per figure — two boards printing the same number can disagree, and nothing on
 * either says which is stale.
 * And the *page key* is all the extension collects of what was open: the F&O
 * form, CE's entity and page type, a Power BI report id, a SharePoint site
 * collection — and for Teams nothing at all, which is why a Teams row can never
 * have a context. No URL, no title, no file name, no field value.
 *
 * ## The states that are not numbers
 *
 * Three of them, and they must never collapse into each other:
 *
 * 1. `access_state !== "ok"` — the workspace could not be read, so no chart
 *    renders at all (the access panel does). An empty axis here would read as a
 *    quiet, healthy estate.
 * 2. An app with no reporting browser — the extension covers it and nobody
 *    posted. A rollout fact, not a fast app, and the reason Section A exists.
 * 3. A `null` median inside a reporting app — the requests carried no timing.
 *    Unknown, never `0 ms`, never green.
 */

import { Chart } from "@norican/dataviz";
import { Factory, Icon, RefreshCw, Rocket, Users } from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  Chip,
  DataGrid,
  EmptyState,
  Freshness,
  PageHeader,
  Skeleton,
  Statistic,
  StatusStrip,
  Typography,
} from "@norican/react";
import { useQueryClient } from "@tanstack/react-query";

import { BoardSection, ChartCard } from "@/components/board";
import { FoAccessPanel, FoErrorsAlert, isPanelOk } from "@/features/dynamics/access-panel";
import { AppChip } from "@/features/dynamics/console-chips";
import { formatDurationCompact, formatDurationMs } from "@/features/dynamics/console-format";
import { BoardCrossLink } from "@/features/dynamics/cross-link";
import { LookbackRow, useLookbackParam } from "@/features/dynamics/lookback-row";
import { PanelCard } from "@/features/dynamics/panel-card";
import { DYNAMICS_PATH } from "@/features/dynamics/params";
import { formatBinDateTime, formatBinTime } from "@/features/dynamics/status";
import { type FoConsoleExperienceApp, useFoConsoleExperience } from "@/lib/api";
import { identityConfig, type StatusTone } from "@/lib/charts/config";
import { useDrillParams } from "@/lib/drill";
import { logger } from "@/lib/logger";
import {
  RUM_APP_SHORT_LABEL,
  RUM_APPS,
  rumAppHasCoverage,
  rumAppLabel,
  rumAppShortLabel,
  sortRumApps,
} from "@/lib/rum-apps";

import { AppTiles } from "./app-tiles";
import { DataSourcesPanel } from "./data-sources";
import {
  appsInHours,
  EM_DASH,
  type ExperienceContextRow,
  experienceContextRows,
  experienceHourRows,
  type ExperienceProbeRow,
  experienceProbeRows,
  foldCaption,
  formatCount,
  PROBE_VERDICT_LABEL,
  THRESHOLD_CAPTION,
} from "./experience-format";
import {
  APP_PARAM,
  EXPERIENCE_CONTEXTS_ANCHOR,
  EXPERIENCE_PROBES_ANCHOR,
  EXPERIENCE_TREND_ANCHOR,
} from "./params";

export { EXPERIENCE_PATH } from "./params";

/** How many page keys the L1 ranking shows before the table twin takes over. */
const WORST_LIMIT = 8;

/** Section anchors. Local, because the two sections are this file's structure. */
const COVERAGE_ANCHOR = "xp-coverage";
const MEASURED_ANCHOR = "xp-measured";

/** The extension id the force-install policy names — pinned by the signing key. */
const EXTENSION_ID = "flfidcbigmdgpcjmpoggkclohnkenphe";

export function ExperiencePage() {
  const queryClient = useQueryClient();
  const lookback = useLookbackParam();
  const experience = useFoConsoleExperience(lookback);
  const drill = useDrillParams(APP_PARAM);
  const selectedApp = drill.params[APP_PARAM] ?? null;

  const data = experience.data;
  const staleAfterMs = data ? data.cache_ttl_sec * 2000 : undefined;

  const refresh = () => {
    logger.debug("[experience] manual refresh requested");
    void queryClient.invalidateQueries({ queryKey: ["fo", "console", "experience"] });
  };

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      {/* The subtitle is this page's own, not the nav registry's: the rail's
          one-liner still promises "what the estate's browsers measure", and the
          measured coverage does not support that sentence. */}
      <PageHeader
        title="Application experience — the browser rollout"
        subtitle="Five apps, measured by one managed Edge extension. How much of the fleet runs it comes first; what those browsers measured comes second, scoped to them."
        actions={
          <div className="flex items-center gap-4">
            {data?.collected_at && (
              <Freshness
                label="Workspace read"
                since={data.collected_at}
                staleAfter={staleAfterMs}
                pulse={experience.isFetching}
              />
            )}
            <Button
              variant="outline"
              tone="neutral"
              startIcon={<Icon icon={RefreshCw} size="sm" />}
              loading={experience.isFetching}
              onClick={refresh}
            >
              Refresh
            </Button>
          </div>
        }
      />

      {experience.error && (
        <Alert tone="danger" title="Couldn't load the experience board">
          The NOVA API did not answer <code>/fo/console/experience</code>. This is a NOVA problem,
          not a browser one.
        </Alert>
      )}

      <LookbackRow scopes="every panel on this board" />

      {experience.isPending && !data && !experience.error && (
        <div className="flex flex-col gap-4" aria-busy="true">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} height="14rem" />
            ))}
          </div>
          <Skeleton height="20rem" />
        </div>
      )}

      {data && !isPanelOk(data) && (
        <FoAccessPanel panel="Application experience (browser RUM)" state={data} />
      )}

      {data && isPanelOk(data) && (
        <ExperienceBody data={data} selectedApp={selectedApp} drill={drill} />
      )}

      {/* Last, and outside every gate above. It explains the numbers rather than
          competing with them — but it is also the panel that tells a calm board
          apart from a stalled one, so it must render precisely when the rest of
          the board cannot: the collector status and the RUM intake come from
          their own endpoints with their own states, and an unreadable experience
          workspace says nothing about whether the collectors ran. */}
      <DataSourcesPanel />
    </main>
  );
}

/**
 * One hour of the trend as the chart kit wants it: the bin's keys plus one
 * median per app. `null` for an app that produced nothing in the hour — never
 * `0`, which would draw a silent app as the fastest one on the board.
 */
type TrendRow = Record<string, string | number | null>;

/** The median for one app in one hour, or `null` — the axis labels are strings. */
function seriesValue(row: TrendRow, app: string): number | null {
  const value = row[app];
  return typeof value === "number" ? value : null;
}

/**
 * The legend/column name for an app id. An id NOVA has no label for renders
 * verbatim, so a sixth app the extension starts reporting on appears by itself
 * (§8) instead of vanishing from the legend.
 */
function appLabelShort(app: string): string {
  return RUM_APP_SHORT_LABEL[app as keyof typeof RUM_APP_SHORT_LABEL] ?? app;
}

/**
 * Every app the board covers, whether or not it produced a row.
 *
 * The `apps` array is the KQL's `summarize … by app`, so an app no browser
 * posted for is **absent** rather than zero. Rendering it verbatim would show
 * only the apps that reported and quietly drop the ones that did not, which is
 * the same failure as a hard-coded subscription list: a smaller, healthier
 * estate than the real one, with no hint that it is doing so. So the five
 * contract ids are put back as explicitly silent rows, and anything the payload
 * carries beyond them is kept.
 */
function rolloutRows(apps: readonly FoConsoleExperienceApp[]): FoConsoleExperienceApp[] {
  const byApp = new Map(apps.map((row) => [row.app, row]));
  const rows = [...byApp.values()];
  for (const app of RUM_APPS) {
    if (!byApp.has(app)) {
      rows.push({
        app,
        users: 0,
        minutes: 0,
        ttfb_p50_ms: null,
        ttfb_p95_ms: null,
        interaction_p50_ms: null,
        interaction_p75_ms: null,
        error_rate: null,
        h3_share: null,
        coverage: null,
      });
    }
  }
  return sortRumApps(rows, (row) => row.app);
}

/** `1 person` / `4 people` — the sample, in the unit the payload actually counts. */
function peopleCount(users: number): string {
  return `${formatCount(users)} ${users === 1 ? "person" : "people"}`;
}

/**
 * "F&O 1 person, 17 min · Teams 4 people, 96 min" — what the charts below rest
 * on, app by app. The sentence a reader needs beside a line drawn from one
 * browser, and the reason it is a caption on every measurement card.
 */
function evidenceCaption(rows: readonly FoConsoleExperienceApp[]): string {
  const reporting = rows.filter((row) => row.users > 0);
  if (reporting.length === 0) {
    return "no browser behind any series";
  }
  return reporting
    .map(
      (row) =>
        `${rumAppShortLabel(row.app)} ${peopleCount(row.users)}, ${formatCount(row.minutes)} min`,
    )
    .join(" · ");
}

interface ExperienceBodyProps {
  data: NonNullable<ReturnType<typeof useFoConsoleExperience>["data"]>;
  selectedApp: string | null;
  drill: ReturnType<typeof useDrillParams<typeof APP_PARAM>>;
}

function ExperienceBody({ data, selectedApp, drill }: ExperienceBodyProps) {
  const staleAfterMs = data.cache_ttl_sec * 2000;

  // Fixed board order, and every covered app present — an app that produced no
  // row is a silent row here, never a missing one.
  const apps = rolloutRows(data.apps);
  const reporting = apps.filter((row) => row.users > 0);
  const nobodyReporting = reporting.length === 0;
  const minutesTotal = apps.reduce((sum, row) => sum + row.minutes, 0);
  const evidence = evidenceCaption(apps);

  const hourApps = sortRumApps(
    appsInHours(data.by_hour).map((app) => ({ app })),
    (row) => row.app,
  ).map((row) => row.app);
  const seriesApps = selectedApp ? hourApps.filter((app) => app === selectedApp) : hourApps;
  const appConfig = identityConfig(hourApps, RUM_APP_SHORT_LABEL);

  // One row per hour, one value per app; an app with no row in an hour keeps a
  // null so the line breaks rather than dropping to zero latency. The chart kit
  // wants flat rows, so the per-app medians are spread beside the axis labels —
  // hence the widened cell type and the `seriesValue` reader below.
  const hourRows: TrendRow[] = experienceHourRows(
    selectedApp ? data.by_hour.filter((point) => point.app === selectedApp) : data.by_hour,
    seriesApps,
  ).map((row) => ({
    ts: row.ts,
    label: formatBinTime(row.ts),
    fullLabel: formatBinDateTime(row.ts),
    ...row.values,
  }));

  const contextRows = experienceContextRows(
    selectedApp
      ? data.worst_contexts.filter((row) => row.app === selectedApp)
      : data.worst_contexts,
  );
  const probeRows = experienceProbeRows(
    selectedApp ? data.probes.filter((row) => row.app === selectedApp) : data.probes,
  ).sort((a, b) => (b.p50_ms ?? -1) - (a.p50_ms ?? -1));

  return (
    <>
      {data.errors.length > 0 && <FoErrorsAlert errors={data.errors} />}

      {/* ── A. Who reports (the rollout) ─────────────────────────────────── */}
      <BoardSection
        id={COVERAGE_ANCHOR}
        eyebrow="Section A"
        title="Who reports"
        lead="One managed Edge extension is the only thing that measures these five apps in the browser, so its reach is the first number on the board rather than a footnote under the charts."
        bottomLine={
          nobodyReporting
            ? "No browser reported in this window. Nothing in Section B measures any of the five apps, and nothing there says any app is fine."
            : `${reporting.length} of ${apps.length} apps have a reporting browser, on ${formatCount(minutesTotal)} user-minutes in total. Section B is what that sample saw — not what the estate experienced.`
        }
      >
        <RolloutPanel
          rows={apps}
          reporting={reporting}
          minutesTotal={minutesTotal}
          payloadApps={data.apps}
        />
        <RolloutGrowthPanel />
      </BoardSection>

      {/* ── B. What those browsers measured ──────────────────────────────── */}
      <BoardSection
        id={MEASURED_ANCHOR}
        eyebrow="Section B"
        title="What those browsers measured"
        lead={
          nobodyReporting
            ? "Nothing measured these apps in this window, so every panel below shows its empty state rather than a chart. An empty axis and a fast app are not the same finding, and this section keeps them apart."
            : `Scoped to the browsers counted above (${evidence}). Every panel here describes that sample and nobody else: a person without the extension has no row anywhere on this page.`
        }
        bottomLine="These are measurements of a sample, not verdicts on an app. F&O's own telemetry — every user, every legal entity, nothing to install — is the Dynamics board's, and it is the population this section is a slice of."
      >
        {nobodyReporting && (
          <Alert tone="neutral" title="No browser has reported yet">
            The intake is reachable and the workspace answered — it simply holds no browser minute
            for this window. Section A says what would change that. Nothing below claims any app is
            fast; the panels render their empty states so the difference stays visible.
          </Alert>
        )}

        <AppTiles
          apps={apps}
          hasDenominator={rumAppHasCoverage}
          selected={selectedApp}
          onSelect={(app) => drill.set({ [APP_PARAM]: app })}
        />

        {/* F&O is one of the five apps here *and* an ERP with a server side. This
            board owns what the browser measured; everything the server did with
            the request — availability, form P95, result codes, Service Bus queues,
            the slow-interaction classification and the Cato network half — is the
            Dynamics board's. One owner per figure, so the two can never disagree. */}
        <BoardCrossLink
          title="Dynamics 365 F&O, server side"
          caption="The F&O tile above is what the reporting browsers measured. F&O's own telemetry covers every user in every legal entity with nothing to install: environment availability, form percentiles, result codes, queue backlog and which layer made an interaction slow are the Dynamics board's numbers, not repeated here."
          to={DYNAMICS_PATH}
          cta="Dynamics board"
          icon={Factory}
        />

        {selectedApp && (
          <div className="flex flex-wrap items-center gap-2">
            <Typography variant="caption" tone="muted">
              Narrowed to:
            </Typography>
            <Chip
              size="sm"
              onRemove={() => drill.set({ [APP_PARAM]: null })}
              removeLabel={`Show every app again instead of only ${rumAppLabel(selectedApp)}`}
            >
              {rumAppLabel(selectedApp)}
            </Chip>
          </div>
        )}

        {/* ── TTFB by hour, one line per app (L1) ───────────────────────── */}
        <section id={EXPERIENCE_TREND_ANCHOR} className="scroll-mt-6">
          <ChartCard<TrendRow>
            title="Time to first byte, hourly median per app"
            caption={`${formatCount(hourRows.length)} hourly bins · drawn from ${evidence} · an hour an app produced no minute is a gap, not a zero`}
            since={data.collected_at}
            staleAfterMs={staleAfterMs}
            tableTwin={{
              id: "xp-ttfb-by-hour",
              ariaLabel: "Median time to first byte per hour and app",
              data: hourRows,
              getRowId: (row) => String(row.ts),
              columns: [
                { id: "hour", header: "Hour", accessor: (row) => String(row.fullLabel ?? "") },
                ...seriesApps.map((app) => ({
                  id: app,
                  header: appLabelShort(app),
                  align: "end" as const,
                  accessor: (row: TrendRow) => seriesValue(row, app),
                  cell: (row: TrendRow) => (
                    <span className="tabular-nums">{formatDurationMs(seriesValue(row, app))}</span>
                  ),
                })),
              ],
            }}
          >
            {hourRows.length === 0 ? (
              <EmptyState
                size="sm"
                title="No hourly bin in this window"
                description="No browser posted a minute for the selected apps, so there is no series to draw. Not a flat line at zero."
              />
            ) : (
              <Chart.Container
                config={appConfig}
                className="h-72"
                aria-label={`Median time to first byte per hour, one line per app, ${data.window}`}
              >
                <Chart.LineChart data={hourRows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                  <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
                  <Chart.XAxis {...Chart.axisProps} dataKey="label" />
                  <Chart.YAxis
                    {...Chart.axisProps}
                    width={52}
                    tickFormatter={(value: number) => formatDurationCompact(value)}
                  />
                  <Chart.Tooltip content={<Chart.TooltipContent />} />
                  {seriesApps.length > 1 && <Chart.Legend content={<Chart.LegendContent />} />}
                  {seriesApps.map((app) => (
                    <Chart.Line
                      key={app}
                      {...Chart.seriesDefaults}
                      dataKey={app}
                      name={appLabelShort(app)}
                      type="monotone"
                      stroke={appConfig[app]?.color}
                      strokeWidth={2}
                      dot={false}
                      connectNulls={false}
                    />
                  ))}
                </Chart.LineChart>
              </Chart.Container>
            )}
          </ChartCard>
        </section>

        {/* ── The worst page keys (L1 "worst N") ────────────────────────── */}
        <section id={EXPERIENCE_CONTEXTS_ANCHOR} className="scroll-mt-6">
          <PanelCard
            title="Slowest pages"
            caption={`by TTFB p95 · ${
              contextRows.length > WORST_LIMIT
                ? `top ${WORST_LIMIT} of ${formatCount(contextRows.length)} — the rest are in View data`
                : `${formatCount(contextRows.length)} page keys`
            }`}
            padding={contextRows.length === 0 ? "md" : "none"}
          >
            {contextRows.length === 0 ? (
              <EmptyState
                size="sm"
                title="No page key was ranked"
                description="No browser minute carried a page key for the selected apps in this window. Teams contributes none by design — the privacy rules collect no page key there at all."
              />
            ) : (
              <ContextsGrid rows={contextRows.slice(0, WORST_LIMIT)} />
            )}
          </PanelCard>
          {/* The fold, the sample and the privacy note sit under the card, so a
              full-bleed grid keeps its own padding and these keep theirs. */}
          <div className="mt-2 flex flex-col gap-1">
            <Typography variant="body-sm" tone="muted">
              The Users column is the whole sample behind that row&apos;s percentile. A page key
              with three people behind it is a hint worth chasing, not a measurement of the page.
            </Typography>
            <Typography variant="body-sm" tone="muted">
              {foldCaption(data.suppression.min_users, data.suppression.folded)}
            </Typography>
            <Typography variant="caption" tone="muted">
              A page key is the app&apos;s own coarse identifier and nothing more: the F&O form,
              CE&apos;s entity and page type, a Power BI report id, a SharePoint site collection. No
              URL, no title, no file name.
            </Typography>
          </div>
        </section>

        {/* ── Idle probes (L1 ranking) ──────────────────────────────────── */}
        <section id={EXPERIENCE_PROBES_ANCHOR} className="scroll-mt-6">
          <ChartCard<ExperienceProbeRow>
            title="Idle probes to each app host"
            caption="one HEAD every five minutes from a browser that has the extension, credentials omitted — the path with nobody using the app"
            since={data.collected_at}
            staleAfterMs={staleAfterMs}
            tableTwin={{
              id: "xp-probes",
              ariaLabel: "Idle probe median first byte per app host",
              data: probeRows,
              getRowId: (row) => `${row.app}:${row.host}`,
              columns: [
                { id: "app", header: "App", accessor: (row) => rumAppLabel(row.app) },
                { id: "host", header: "Host", accessor: (row) => row.host },
                {
                  id: "p50",
                  header: "TTFB p50",
                  align: "end",
                  accessor: (row) => row.p50_ms,
                  cell: (row) => (
                    <span className="tabular-nums">{formatDurationMs(row.p50_ms)}</span>
                  ),
                },
                {
                  id: "probes",
                  header: "Probes",
                  align: "end",
                  accessor: (row) => row.samples,
                  cell: (row) => <span className="tabular-nums">{formatCount(row.samples)}</span>,
                },
                {
                  id: "failures",
                  header: "No answer",
                  align: "end",
                  accessor: (row) => row.failures,
                  cell: (row) => <span className="tabular-nums">{formatCount(row.failures)}</span>,
                },
                { id: "verdict", header: "Verdict", accessor: (row) => row.detail },
              ],
            }}
          >
            {probeRows.length === 0 ? (
              <EmptyState
                size="sm"
                title="No probe reached a host"
                description="The idle probe runs from the extension, so it needs at least one browser with it installed and open. Nothing is known about these paths — which is not the same as their being fine."
              />
            ) : (
              <div className="flex flex-col gap-3">
                <Chart.Container
                  config={{ p50_ms: { label: "TTFB p50", color: "var(--norican-chart-1)" } }}
                  className="h-64"
                  aria-label="Median first byte of the idle probe per app host, slowest first"
                >
                  <Chart.BarChart
                    data={probeRows}
                    layout="vertical"
                    margin={{ top: 8, right: 16, bottom: 0, left: 8 }}
                  >
                    <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
                    <Chart.XAxis
                      {...Chart.axisProps}
                      type="number"
                      tickFormatter={(value: number) => formatDurationCompact(value)}
                    />
                    <Chart.YAxis {...Chart.axisProps} type="category" dataKey="host" width={190} />
                    <Chart.Tooltip
                      cursor={{ fill: "var(--norican-surface-hover)" }}
                      content={
                        <Chart.TooltipContent
                          rows={(datum: ExperienceProbeRow) => [
                            { label: "TTFB p50", value: formatDurationMs(datum.p50_ms) },
                            { label: "Probes", value: formatCount(datum.samples) },
                            { label: "No answer", value: formatCount(datum.failures) },
                            { label: "Verdict", value: PROBE_VERDICT_LABEL[datum.verdict] },
                          ]}
                        />
                      }
                    />
                    <Chart.Bar {...Chart.seriesDefaults} dataKey="p50_ms" radius={[0, 3, 3, 0]}>
                      {probeRows.map((row) => (
                        <Chart.Cell
                          key={`${row.app}:${row.host}`}
                          fill={
                            row.tone === "danger"
                              ? "var(--norican-danger)"
                              : row.tone === "warning"
                                ? "var(--norican-warning)"
                                : row.tone === "success"
                                  ? "var(--norican-chart-1)"
                                  : "var(--norican-bg-muted)"
                          }
                        />
                      ))}
                    </Chart.Bar>
                  </Chart.BarChart>
                </Chart.Container>
                {/* The bar colour is a status, so it is never colour-alone, and
                    each line carries the probe count it was measured over. */}
                <ul className="flex flex-col gap-1">
                  {probeRows.map((row) => (
                    <li
                      key={`${row.app}:${row.host}`}
                      className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs"
                    >
                      <AppChip app={row.app} short />
                      <code className="break-all">{row.host}</code>
                      <Badge
                        tone={
                          row.tone === "neutral"
                            ? "neutral"
                            : row.tone === "success"
                              ? "success"
                              : row.tone
                        }
                        variant="subtle"
                        size="xs"
                      >
                        {PROBE_VERDICT_LABEL[row.verdict]}
                      </Badge>
                      <span className="text-fg-muted">{row.detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </ChartCard>
        </section>

        <Typography variant="caption" tone="muted">
          {THRESHOLD_CAPTION} Coverage exists for F&O only: nothing in this workspace counts the
          people who used the other four apps without the extension, so their user counts are lower
          bounds.
        </Typography>
      </BoardSection>
    </>
  );
}

/* ------------------------------------------------------------------------ *
 * Section A — the rollout
 * ------------------------------------------------------------------------ */

interface RolloutPanelProps {
  /** Every covered app, silent ones included. */
  rows: readonly FoConsoleExperienceApp[];
  /** Of those, the ones a browser posted for. */
  reporting: readonly FoConsoleExperienceApp[];
  /** User-minutes summed across apps. */
  minutesTotal: number;
  /** The payload's own rows — a coverage ratio only exists on one of these. */
  payloadApps: readonly FoConsoleExperienceApp[];
}

/**
 * The coverage answer, as three numbers and a per-app strip.
 *
 * The tones are the reserved status scale used as §5 prescribes — coverage
 * below threshold is danger, not a neutral fact — and no cell is colour-alone:
 * every one carries its count or the word "silent".
 */
function RolloutPanel({ rows, reporting, minutesTotal, payloadApps }: RolloutPanelProps) {
  const appsTone: StatusTone =
    reporting.length === 0 ? "danger" : reporting.length < rows.length ? "warning" : "success";

  // Only a row the payload actually returned can carry the F&O denominator; a
  // silent placeholder cannot, and must not imply the query answered.
  const coverage = payloadApps.find((row) => row.app === "fo")?.coverage ?? null;
  const pct = coverage != null && coverage.total > 0 ? coverage.pct : null;
  const coverageTone: StatusTone =
    pct == null ? "neutral" : pct >= 75 ? "success" : pct >= 40 ? "warning" : "danger";

  return (
    <PanelCard
      title="Who reports"
      caption="counts are distinct people, not machines — the extension posts under the signed-in user, so a laptop and a desktop are one person here"
    >
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Statistic
            label="Apps with a reporting browser"
            value={`${formatCount(reporting.length)} of ${formatCount(rows.length)}`}
            tone={appsTone}
            size="lg"
            accent
            caption="Counted over the apps the extension covers. An app at zero is not a fast app: it is an app nothing on this board measures."
          />
          <Statistic
            label="F&O users with a reporting browser"
            value={
              pct == null || coverage == null
                ? EM_DASH
                : `${formatCount(coverage.with_rum)} of ${formatCount(coverage.total)}`
            }
            tone={coverageTone}
            size="lg"
            accent
            progress={pct ?? undefined}
            progressTone={coverageTone === "neutral" ? "accent" : coverageTone}
            caption={
              pct == null
                ? "No denominator answered in this window, so the share is unknown — not 0 %, and not full coverage. F&O is the only app with a denominator at all."
                : `${pct.toFixed(1)} % of the people who opened an F&O form in this window. The rest have no browser block on any record, here or on the Dynamics board.`
            }
          />
          <Statistic
            label="Evidence behind Section B"
            value={formatCount(minutesTotal)}
            tone="neutral"
            size="lg"
            accent
            caption="User-minutes with at least one row, summed across apps (a minute with two apps open counts once per app). Every median and percentile below rests on this and nothing else."
          />
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Icon icon={Users} size="sm" aria-hidden="true" />
            <Typography variant="caption" tone="muted" as="h3" className="font-semibold uppercase">
              Per app, this window
            </Typography>
          </div>
          <StatusStrip aria-label="People reporting per app in this window">
            {rows.map((row) => (
              <StatusStrip.Cell
                key={row.app}
                status={row.users > 0 ? "positive" : "neutral"}
                label={rumAppShortLabel(row.app)}
                title={
                  row.users > 0
                    ? `${rumAppLabel(row.app)}: ${peopleCount(row.users)} reporting over ${formatCount(row.minutes)} user-minutes`
                    : `${rumAppLabel(row.app)}: no browser posted a minute in this window`
                }
              >
                {row.users > 0 ? formatCount(row.users) : "silent"}
              </StatusStrip.Cell>
            ))}
          </StatusStrip>
          <Typography variant="caption" tone="muted">
            An app the payload returned no row for is drawn here as silent rather than left out, so
            a board that measures one app cannot look like a board that measures five.
          </Typography>
        </div>
      </div>
    </PanelCard>
  );
}

/**
 * What would make the numbers above grow — the `/cloud` setup panel's job, for a
 * rollout rather than a credential.
 *
 * A flat line with no explanation invites the reader to conclude the apps are
 * fine. This panel is the alternative: the three levers that exist, in the order
 * of how much they move, plus the two things NOVA cannot see about the fleet so
 * nobody mistakes silence here for a fleet that is up to date.
 */
function RolloutGrowthPanel() {
  const updateUrl = `${window.location.origin}/ext/fo-rum/update.xml`;

  return (
    <Card variant="outline" accent="left" accentTone="accent">
      <Card.Header>
        <div className="flex items-center gap-3">
          <Icon icon={Rocket} size="md" />
          <Typography variant="heading-4" as="h2">
            What makes these numbers grow
          </Typography>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="flex flex-col gap-4">
          <Typography variant="body-sm" tone="muted">
            Coverage is a distribution question, not a measurement one. NOVA serves the package; a
            browser installs it, and Edge keeps it current on its own. Three levers, in the order of
            how far each one reaches:
          </Typography>
          <ul className="text-fg-muted flex list-disc flex-col gap-1.5 pl-5 text-sm">
            <li>
              <span className="text-fg font-medium">In Intune</span> — force-install it on managed
              Edge: Settings catalog → Microsoft Edge → Extensions → &ldquo;Control which extensions
              are installed silently&rdquo;, with{" "}
              <code className="text-fg break-all">
                {EXTENSION_ID};{updateUrl}
              </code>
              . Every enrolled machine then installs it with nothing for the user to do.
            </li>
            <li>
              <span className="text-fg font-medium">On machines that already have it</span> — Edge
              re-reads{" "}
              <a
                href={updateUrl}
                target="_blank"
                rel="noreferrer"
                className="text-accent rounded hover:underline focus-visible:outline-2"
              >
                that update manifest
              </a>{" "}
              roughly every five hours and at browser start, so a newly published build reaches the
              fleet within a few hours by itself. There is nothing to push per machine, and no
              reason to touch a machine that is only behind.
            </li>
            <li>
              <span className="text-fg font-medium">On one machine, without a policy</span> —{" "}
              <code className="text-fg">edge://extensions</code> → Developer mode → Load unpacked,
              from <code className="text-fg break-all">/ext/fo-rum/fo-rum.zip</code> on this host.
              Enough to prove the path end to end; not a rollout.
            </li>
          </ul>
          <Typography variant="body-sm" tone="muted">
            Two things this board cannot see, and should not be read as answering: which{" "}
            <strong className="text-fg">version</strong> each browser runs, and how many{" "}
            <strong className="text-fg">machines</strong> report. Every row carries{" "}
            <code className="text-fg">ExtVersion</code> and{" "}
            <code className="text-fg">InstallId</code> into the workspace, but no NOVA endpoint
            groups by either, so the counts above are distinct people and a fleet still on an old
            build looks exactly like a fleet on the newest one.
          </Typography>
          <Typography variant="caption" tone="muted">
            The policy string above names the NOVA host you are reading this on, because that is the
            host serving the package; a fleet-wide policy must name the production one. Whether the
            intake itself is on, and when a browser last posted, is the Data sources panel at the
            foot of this page.
          </Typography>
        </div>
      </Card.Body>
    </Card>
  );
}

function ContextsGrid({ rows }: { rows: ExperienceContextRow[] }) {
  return (
    <div className="h-[24rem]">
      {/* DataGrid needs a definite height for its sticky header and scroller. */}
      <DataGrid<ExperienceContextRow>
        data={rows}
        getRowId={(row) => `${row.app}:${row.context}`}
        density="compact"
        aria-label="Slowest page keys by 95th-percentile time to first byte"
        columns={[
          {
            id: "app",
            header: "App",
            accessor: (row) => row.app,
            sortable: true,
            cell: (row) => <AppChip app={row.app} short />,
          },
          {
            id: "context",
            header: "Page key",
            accessor: (row) => row.label,
            sortable: true,
            cell: (row) =>
              row.is_other ? (
                <span className="text-fg-muted inline-flex items-center gap-2 text-sm italic">
                  {row.label}
                  <Badge tone="neutral" variant="outline" size="xs">
                    folded cohort
                  </Badge>
                </span>
              ) : (
                <code className="text-xs break-all">{row.label}</code>
              ),
          },
          {
            id: "users",
            header: "Users",
            align: "end",
            accessor: (row) => row.users,
            sortable: true,
            cell: (row) =>
              row.users == null ? (
                <span className="text-fg-subtle text-xs" title={row.usersNote ?? undefined}>
                  {row.usersNote ?? "—"}
                </span>
              ) : (
                <span className="tabular-nums">{formatCount(row.users)}</span>
              ),
          },
          {
            id: "ttfb",
            header: "TTFB p95",
            align: "end",
            accessor: (row) => row.ttfb_p95_ms,
            sortable: true,
            cell: (row) => (
              <span className="tabular-nums">{formatDurationMs(row.ttfb_p95_ms)}</span>
            ),
          },
          {
            id: "verdict",
            header: "Against the board's threshold",
            accessor: (row) => row.ttfbTone,
            cell: (row) =>
              row.ttfb_p95_ms == null ? (
                <span className="text-fg-muted text-xs">no percentile — unknown, not fast</span>
              ) : (
                <Badge
                  tone={row.ttfbTone === "neutral" ? "neutral" : row.ttfbTone}
                  variant="subtle"
                  size="xs"
                >
                  {row.ttfbTone === "success"
                    ? "within threshold"
                    : row.ttfbTone === "warning"
                      ? "slow"
                      : "bad"}
                </Badge>
              ),
          },
        ]}
      />
    </div>
  );
}
